/**
 * Servicio para manejar imágenes de usuario en Supabase y Firebase Storage.
 * Adaptado para la versión web usando Firebase Storage como en la app móvil.
 */

import { supabase } from '../lib/supabase';
import { ref, deleteObject, uploadBytesResumable, getDownloadURL, uploadBytes } from 'firebase/storage';
import { storage } from '../lib/firebase';

/**
 * Obtiene la lista de imágenes del usuario en un álbum específico.
 * Recibe userId y album (nombre del álbum permanente), devuelve lista de imágenes.
 * Usa la tabla album_images para las relaciones.
 */
export const getUserImages = async (userId, album) => {
  // Para álbumes permanentes del sistema (privado-1, fotosDePerfil)
  // Primero obtener el ID del álbum por nombre
  let albumData;

  const { data: albumQuery, error: queryError } = await supabase
    .from('albums')
    .select('id')
    .eq('name', album)
    .eq('type', 1) // type=1 para álbumes personales
    .eq('user_id', userId)
    .maybeSingle(); // maybeSingle permite null sin error

  if (queryError) {
    console.error('Error obteniendo álbum:', queryError?.message);
    throw new Error('Error obteniendo álbum');
  }

  // Si el álbum no existe, crearlo automáticamente (para álbumes permanentes del sistema)
  if (!albumQuery) {
    console.log('Álbum no encontrado, creando automáticamente:', album);
    const { data: newAlbum, error: createError } = await supabase
      .from('albums')
      .insert({
        user_id: userId,
        name: album,
        type: 1,
        is_active: true,
        is_deleted: false
      })
      .select('id')
      .single();

    if (createError) {
      console.error('Error creando álbum:', createError?.message);
      throw new Error('Error creando álbum');
    }

    albumData = newAlbum;
  } else {
    albumData = albumQuery;
  }

  console.log('[getUserImages] Buscando imágenes para álbum:', { album, albumId: albumData.id, userId });

  // Obtener imágenes a través de la relación album_images
  const { data, error } = await supabase
    .from('album_images')
    .select(`
      user_images (
        img_id,
        url,
        storageId,
        created_at,
        user_ref_id
      )
    `)
    .eq('album_id', albumData.id)
    .eq('user_images.is_deleted', false);

  if (error) {
    console.error('Error obteniendo imágenes:', error.message);
    throw error;
  }

  // Transformar datos al formato esperado
  const images = data?.map(item => item.user_images).filter(img => img !== null) || [];
  
  // Ordenar en JavaScript por created_at descendente (más reciente primero)
  images.sort((a, b) => new Date(b.created_at) - new Date(a.created_at));
  
  console.log('[getUserImages] Imágenes obtenidas (ordenadas por created_at DESC):', {
    total: images.length,
    album,
    images: images.map(img => ({
      img_id: img.img_id,
      url: img.url,
      created_at: img.created_at,
      storageId: img.storageId,
      user_ref_id: img.user_ref_id
    }))
  });

  return images;
};

/**
 * Inserta una nueva imagen en la tabla user_images y la relaciona con el álbum.
 * Recibe userId, url, album (nombre), storageId.
 */
export const insertUserImage = async (userId, url, album, storageId) => {
  // Obtener el ID del álbum por nombre
  let albumData;

  const { data: albumQuery, error: queryError } = await supabase
    .from('albums')
    .select('id')
    .eq('name', album)
    .eq('type', 1)
    .eq('user_id', userId)
    .maybeSingle();

  if (queryError) {
    console.error('Error obteniendo álbum:', queryError?.message);
    throw new Error('Error obteniendo álbum');
  }

  // Si el álbum no existe, crearlo automáticamente
  if (!albumQuery) {
    console.log('Álbum no encontrado, creando automáticamente:', album);
    const { data: newAlbum, error: createError } = await supabase
      .from('albums')
      .insert({
        user_id: userId,
        name: album,
        type: 1,
        is_active: true,
        is_deleted: false
      })
      .select('id')
      .single();

    if (createError) {
      console.error('Error creando álbum:', createError?.message);
      throw new Error('Error creando álbum');
    }

    albumData = newAlbum;
  } else {
    albumData = albumQuery;
  }

  // Insertar imagen en user_images
  const { data: imageData, error: imageError } = await supabase
    .from('user_images')
    .insert({
      user_ref_id: userId,
      url: url,
      storageId: storageId
    })
    .select()
    .single();

  if (imageError) {
    console.error('Error insertando imagen:', imageError.message);
    throw imageError;
  }

  // Relacionar imagen con álbum en album_images
  const { error: relationError } = await supabase
    .from('album_images')
    .insert({
      album_id: albumData.id,
      image_id: imageData.img_id
    });

  if (relationError) {
    console.error('Error relacionando imagen con álbum:', relationError.message);
    throw relationError;
  }

  return imageData;
};

/**
 * Soft delete: marca imagen como eliminada en Supabase.
 * Recibe imgId, album (nombre), storageId.
 */
export const softDeleteUserImage = async (imgId, album, storageId) => {
  console.log('[SOFT DELETE DB] Marcando imagen como eliminada:', { imgId, album, storageId });
  // Marca imagen como eliminada en user_images
  const { error } = await supabase
    .from('user_images')
    .update({ 
      is_deleted: true, 
      deleted_at: new Date().toISOString() 
    })
    .eq('img_id', imgId)
    .eq('storageId', storageId);

  if (error) {
    console.error('[SOFT DELETE DB] Error en soft delete de imagen en DB:', error.message);
    throw error;
  }
  console.log('[SOFT DELETE DB] Imagen marcada como eliminada exitosamente');
};

/**
 * Comprime y valida imagen para web (simplificado, sin recorte nativo).
 * Recibe file y maxSizeMB, devuelve objeto con blob y success.
 */
const compressAndValidateImage = async (file, maxSizeMB = 2) => {
  try {
    // Validar tamaño
    const maxSizeBytes = maxSizeMB * 1024 * 1024;
    if (file.size > maxSizeBytes) {
      console.error('Imagen excede el tamaño máximo:', maxSizeMB, 'MB');
      return { success: false, error: 'Imagen excede el tamaño máximo' };
    }

    // Validar tipo
    if (!file.type.startsWith('image/')) {
      console.error('Archivo no es una imagen:', file.type);
      return { success: false, error: 'Archivo no es una imagen' };
    }

    // En web, simplemente devolvemos el blob del archivo
    // Para compresión real se necesitaría canvas o una librería como browser-image-compression
    return { 
      success: true, 
      blob: file,
      originalSize: file.size
    };
  } catch (error) {
    console.error('Error comprimiendo/validando imagen:', error);
    return { success: false, error: error.message };
  }
};

/**
 * Sube una imagen al storage y guarda en DB.
 * Recibe album, userId y file (File object del input de archivo), devuelve resultado de upload.
 */
export const uploadImage = async (album, userId, file) => {
  // Genera storageId único.
  var characters = '0123456789';
  var storageId = '';

  for (var i = 0; i < 8; i++) {
    storageId += characters.charAt(Math.floor(Math.random() * characters.length));
  }

  if (!file) {
    console.log('No se seleccionó imagen');
    return { statusResponse: false };
  }

  try {
    // Comprimir y validar imagen
    const compressed = await compressAndValidateImage(file, 2);

    if (!compressed.success) {
      console.error('Error en compresión/validación:', compressed.error);
      // Lanza error con el mensaje específico para que el hook pueda mostrarlo
      throw new Error(compressed.error);
    }

    // Sube imagen a Firebase Storage
    const metadata = {
      contentType: file.type || 'image/jpeg',
      customMetadata: { uploadedBy: userId, storageId: storageId },
    };

    const uploadTask = uploadBytesResumable(
      ref(storage, userId + '/photos/' + storageId), 
      compressed.blob, 
      metadata
    );

    return new Promise((resolve, reject) => {
      const result = { statusResponse: false, error: null, url: null, storageId: null };

      uploadTask.on('state_changed',
        (snapshot) => {
          const progress = (snapshot.bytesTransferred / snapshot.totalBytes) * 100;
          console.log('Upload ' + progress + '% done');
        },
        (error) => {
          console.error('Error en upload:', error.message);
          result.error = error.message;
          reject(result);
        },
        () => {
          getDownloadURL(uploadTask.snapshot.ref)
            .then((downloadURL) => {
              console.log('Archivo disponible en', downloadURL);
              result.url = downloadURL;
              result.storageId = storageId;
              result.statusResponse = true;
              resolve(result);
            })
            .catch((error) => {
              console.error('Error obteniendo URL:', error.message);
              result.error = error.message;
              reject(result);
            });
        }
      );
    });
  } catch (error) {
    console.error('Error en upload:', error);
    // Propagar el error para que el hook pueda mostrar el mensaje específico
    throw error;
  }
};

/**
 * Soft delete: mueve imagen a carpeta deleted/ en Firebase Storage.
 * Recibe storageId y userId.
 * Es idempotente: si el archivo ya está en deleted/ o no existe, retorna éxito.
 */
export const softDeleteImageFromStorage = async (storageId, userId) => {
  console.log('[SOFT DELETE STORAGE] Iniciando soft delete para storageId:', storageId, 'userId:', userId);
  try {
    const oldRef = ref(storage, `${userId}/photos/${storageId}`);
    const newRef = ref(storage, `${userId}/deleted/${storageId}`);
    
    console.log('[SOFT DELETE STORAGE] Ruta origen:', `${userId}/photos/${storageId}`);
    console.log('[SOFT DELETE STORAGE] Ruta destino:', `${userId}/deleted/${storageId}`);
    
    // Verificar si el archivo ya está en deleted/
    try {
      await getDownloadURL(newRef);
      console.log('[SOFT DELETE STORAGE] Archivo ya existe en deleted/, no es necesario moverlo');
      return { success: true, alreadyDeleted: true };
    } catch (error) {
      // Archivo no está en deleted/, continuar con el proceso normal
      console.log('[SOFT DELETE STORAGE] Archivo no está en deleted/, continuando proceso');
    }
    
    // Verificar si el archivo existe en photos/
    try {
      const url = await getDownloadURL(oldRef);
      console.log('[SOFT DELETE STORAGE] URL obtenida:', url);
      
      const response = await fetch(url);
      const blob = await response.blob();
      console.log('[SOFT DELETE STORAGE] Blob obtenido, tamaño:', blob.size, 'bytes');
      
      // Subir a carpeta deleted
      await uploadBytes(newRef, blob);
      console.log('[SOFT DELETE STORAGE] Archivo subido a deleted/');
      
      // Eliminar de carpeta original
      await deleteObject(oldRef);
      console.log('[SOFT DELETE STORAGE] Archivo eliminado de photos/');
      
      return { success: true };
    } catch (error) {
      // Archivo no existe en photos/, probablemente ya fue eliminado
      console.log('[SOFT DELETE STORAGE] Archivo no existe en photos/, asumiendo que ya fue eliminado');
      return { success: true, alreadyDeleted: true };
    }
  } catch (error) {
    console.error('[SOFT DELETE STORAGE] Error en soft delete del storage:', error);
    return { success: false, error };
  }
};
